import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Send, X, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that lets a teacher assign a chapter-level practice to one or
 * more students. Pre-selects the source student and lets the teacher
 * tick classmates so the same practice goes to anyone with similar gaps.
 */
const AssignPracticeModal = ({ open, onClose, sourceStudent, classLevel, subject, chapterName, actionTitle }) => {
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !classLevel) return;
    setLoading(true);
    axios.get(`${API}/teacher/class-students-list`, { params: { class_level: classLevel } })
      .then(res => {
        setStudents(res.data || []);
        // Pre-select the source student
        const initial = new Set();
        if (sourceStudent?.student_id) initial.add(sourceStudent.student_id);
        setSelectedIds(initial);
      })
      .catch(() => toast.error('Failed to load student list'))
      .finally(() => setLoading(false));
  }, [open, classLevel, sourceStudent]);

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAssign = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one student');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/teacher/practice-assignment`, {
        student_ids: Array.from(selectedIds),
        class_level: classLevel,
        subject,
        chapter_name: chapterName,
        title: actionTitle || `Practice — ${chapterName || subject || 'general concepts'}`,
        note: actionTitle || '',
      });
      toast.success(`Assigned to ${selectedIds.size} student${selectedIds.size === 1 ? '' : 's'}`);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign practice');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="assign-practice-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="font-bold text-lg text-gray-800">Assign Practice</h3>
            <p className="text-sm text-gray-500 mt-1">
              {actionTitle ? <span>"{actionTitle}"</span> : 'Select students to assign this practice to'}
            </p>
            {chapterName && (
              <p className="text-xs text-indigo-600 mt-1">Chapter: <strong>{chapterName}</strong>{subject ? ` · ${subject}` : ''}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
              <Users size={14} /> Class {classLevel} students ({students.length})
            </h4>
            <button
              onClick={() => {
                if (selectedIds.size === students.length) setSelectedIds(new Set());
                else setSelectedIds(new Set(students.map(s => s.id)));
              }}
              className="text-xs text-indigo-600 hover:underline"
              data-testid="select-all-students"
            >
              {selectedIds.size === students.length ? 'Clear all' : 'Select all'}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="mx-auto animate-spin text-indigo-500" /></div>
          ) : (
            <div className="space-y-1.5">
              {students.map(s => {
                const isSrc = s.id === sourceStudent?.student_id;
                const checked = selectedIds.has(s.id);
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      checked ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`student-row-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(s.id)}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{s.name}</p>
                      <p className="text-xs text-gray-500">Roll {s.roll_number}</p>
                    </div>
                    {isSrc && (
                      <span className="text-[10px] uppercase font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">Source</span>
                    )}
                  </label>
                );
              })}
              {students.length === 0 && (
                <p className="text-center text-gray-400 py-6 text-sm">No students in this class yet.</p>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-between gap-2">
          <p className="text-sm text-gray-600">
            <strong>{selectedIds.size}</strong> student{selectedIds.size === 1 ? '' : 's'} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleAssign}
              disabled={submitting || selectedIds.size === 0}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
              data-testid="assign-practice-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
              Assign Practice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignPracticeModal;
